import { useTrans } from '@/shared/hooks/useTranslation';
import { type TrialView, useEntitlementsQuery } from '@/viewmodel/features/entitlements';
import { usePlanLimitToastAction } from '@/viewmodel/features/etlSource';
import { useDismissedTrialPhases } from '@/viewmodel/features/quota/hooks/useDismissedTrialPhases';
import {
  trialBannerText,
  trialClockText,
  trialLockText,
  trialPhase,
} from '@/viewmodel/features/quota/mappers/trialText';
import { writeDismissedPhase } from '@/viewmodel/features/quota/state/trialBannerDismissal';
import type {
  TrialBannerView,
  TrialClockView,
  TrialLockView,
} from '@/viewmodel/features/quota/types';

interface ShellTrial {
  readonly trial: TrialView;
  readonly workspaceId: string;
  readonly dismissed: boolean;
  readonly dismiss: () => void;
}

function useShellTrial(): ShellTrial | null {
  const { trial, workspaceId } = useEntitlementsQuery();
  const dismissedPhases = useDismissedTrialPhases();
  if (!trial?.started || !workspaceId) return null;

  const phase = trialPhase(trial);

  return {
    trial,
    workspaceId,
    dismissed: phase !== 'expired' && dismissedPhases[workspaceId] === phase,
    dismiss: () => {
      writeDismissedPhase(workspaceId, phase);
    },
  };
}

export function useTrialBannerViewModel(): TrialBannerView | null {
  const { t } = useTrans();
  const shell = useShellTrial();
  const planAction = usePlanLimitToastAction();
  if (!shell || shell.dismissed) return null;

  return {
    ...trialBannerText(shell.trial, t),
    expired: shell.trial.expired,
    actionLabel: planAction.label,
    onAction: planAction.onPress,
    dismissLabel: t('trial.banner.dismiss'),
    onDismiss: shell.trial.expired ? null : shell.dismiss,
  };
}

export function useTrialClockViewModel(): TrialClockView | null {
  const { t } = useTrans();
  const shell = useShellTrial();
  const planAction = usePlanLimitToastAction();
  if (!shell?.dismissed) return null;

  return {
    ...trialClockText(shell.trial, t),
    expired: shell.trial.expired,
    onPress: planAction.onPress,
  };
}

export function useTrialLockViewModel(): TrialLockView | null {
  const { t } = useTrans();
  const { trial } = useEntitlementsQuery();
  const planAction = usePlanLimitToastAction();
  if (!trial?.expired) return null;

  return { ...trialLockText(t), actionLabel: planAction.label, onAction: planAction.onPress };
}
